import { useState } from 'react';
import { useIntl } from 'react-intl';
import {
  ButtonUI,
  Input,
  InputSearch,
  MultipleTagSelector,
  showNotification,
  TaptapList,
  TooltipButton,
  useQueryFetch
} from 'sonata-react-components';

import './ConversionTracking.scss';

import { formatDate } from 'sonata-static-storage/lib/helpers/generalFormatters.helpers';

import { copyPixelCodeToClipboard } from '@/helpers/pixel/pixel.helper';
import { getPixels } from '@/services/pixel.service';
import { useGlobalStore } from '@/store/global';
import { getUser } from '@/store/global/user/selectors';
import { FormExpandableSection } from '../FormExpandableSection';
import ConversionTrackingModal from './ConversionTrackingModal/ConversionTrackingModal';
import { getConversionTrackingTags } from './helpers/getSectionHeaderTags.helper';

import type { Pixel, PixelAssociatedEntity } from 'sonata-static-storage/lib/schemas/pixel/pixel.schema';

type TaptapListItem = {
  title: string;
  sonataPid: string;
  type: string;
  creationDate: string;
  postViewWindow: string;
  postClickWindow: string;
  actions: any[];
  id: string;
};

type State = {
  search: string | null;
  selectedPixelsRows: PixelAssociatedEntity[];
  showModal: boolean;
  statusFilter: 'all' | 'enabled' | 'disabled';
};

type Props = {
  id: string;
  onPixelsChange?: (pixels: PixelAssociatedEntity[]) => void;
  pixels?: PixelAssociatedEntity[];
};

const DEFAULT_STATE_VALUE: State = {
  search: null,
  selectedPixelsRows: [],
  showModal: false,
  statusFilter: 'all'
};

const POST_VIEW_DEFAULT = 90;
const POST_CLICK_DEFAULT = 90;

function ConversionTracking({ id, onPixelsChange, pixels }: Props) {
  const { formatMessage } = useIntl();

  const user = useGlobalStore(getUser);
  const userJSON = user.toJSON();
  const token: Record<string, any> = {};

  const { data: userPixels } = useQueryFetch(
    ['pixels', pixels?.map(e => e.pixelId)],
    async () => getPixels({ pixelsIds: pixels?.map(e => e.pixelId) }, userJSON.id!, token),
    {
      enabled: !!pixels?.length
    }
  );

  const [{ search, selectedPixelsRows, showModal, statusFilter }, setState] = useState<State>({
    ...DEFAULT_STATE_VALUE,
    selectedPixelsRows: pixels || DEFAULT_STATE_VALUE.selectedPixelsRows
  });

  const handleSearchChange = (value: string) => {
    setState(prevState => ({
      ...prevState,
      search: value
    }));
  };

  const handleStatusChange = (values: string[]) => {
    const value = (values[0] ?? 'all') as 'all' | 'enabled' | 'disabled';
    setState(prevState => ({
      ...prevState,
      statusFilter: value
    }));
  };

  const handlePixelsSave = (pixels: Pixel[]) => {
    const pixelMapped: PixelAssociatedEntity[] = pixels.map(p => ({
      created: p.created,
      enabled: true,
      name: p.name,
      pixelId: p._id!,
      placement: p.placement,
      postClickWindow: POST_CLICK_DEFAULT,
      postViewWindow: POST_VIEW_DEFAULT,
      type: p.type
    }));
    setState(prevState => ({
      ...prevState,
      selectedPixelsRows: pixelMapped
    }));
    onPixelsChange?.(pixelMapped);
  };

  const handleUpdatePixels = (pixelId: string, changes: Partial<PixelAssociatedEntity>) => {
    const updatedPixels = selectedPixelsRows.map(p => (p.pixelId === pixelId ? { ...p, ...changes } : p));
    setState(prevState => ({
      ...prevState,
      selectedPixelsRows: updatedPixels
    }));
    onPixelsChange?.(updatedPixels);
  };

  const handleCopyPixelCode = async (pixel: PixelAssociatedEntity) => {
    await copyPixelCodeToClipboard({ pixel });
    showNotification(
      'map',
      formatMessage({
        defaultMessage: 'Pixel code copied to clipboard',
        id: 'conversionTracking.modal.action.copied'
      })
    );
  };

  const handleDeletePixels = (pixelId: string) => {
    const updatedPixels = selectedPixelsRows.filter(p => p.pixelId !== pixelId);
    setState(prevState => ({
      ...prevState,
      selectedPixelsRows: updatedPixels
    }));
    onPixelsChange?.(updatedPixels);
  };

  const handleResetConversionTracking = () => {
    setState({ ...DEFAULT_STATE_VALUE });
    onPixelsChange?.([]);
  };

  const filteredData = selectedPixelsRows.filter(pixel => {
    if (search) {
      const userPixel = userPixels?.items.find(up => up._id === pixel.pixelId);
      const searchLower = search.toLowerCase();
      const matchesSearch =
        userPixel?.name.toLowerCase().includes(searchLower) || userPixel?.type.toLowerCase().includes(searchLower);

      if (!matchesSearch) {
        return false;
      }
    }

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'enabled' && pixel.enabled) ||
      (statusFilter === 'disabled' && !pixel.enabled);

    return matchesStatus;
  });

  const compositionData = filteredData.map(pixel => {
    const userPixel = userPixels?.items.find(up => up._id === pixel.pixelId);
    return {
      actions: [
        {
          icon: 'fal fa-trash',
          id: `conversion-tracking-delete-${pixel.pixelId}`,
          onClick: () => handleDeletePixels(pixel.pixelId),
          size: 'small'
        },
        {
          content: (
            <TooltipButton
              buttonConfig={{
                buttonOnClick: () => handleCopyPixelCode(pixel),
                buttonShape: 'link',
                buttonSize: 'ultraSmall',
                buttonType: 'secondary'
              }}
              icon="fa-solid fa-code"
              id={`conversion-tracking-copy-tooltip-${pixel.pixelId}`}
              isButton={true}
              tooltipConfig={{
                tooltipEvent: 'mouseenter',
                tooltipPlace: 'left'
              }}
            >
              {formatMessage({
                defaultMessage: "Copy Pixel's code",
                id: 'conversionTracking.modal.action.copy'
              })}
            </TooltipButton>
          ),
          id: `custom-action-edit-tooltip-${pixel.pixelId}`,
          isCustomAction: true
        },
        {
          content: (
            <TooltipButton
              buttonConfig={{
                buttonOnClick: () => handleUpdatePixels(pixel.pixelId, { enabled: !pixel.enabled }),
                buttonShape: 'link',
                buttonSize: 'ultraSmall',
                buttonType: 'secondary'
              }}
              icon={pixel.enabled ? 'fa-regular fa-eye' : 'fa-regular fa-eye-slash'}
              id={`conversion-tracking-show-tooltip-${pixel.pixelId}`}
              isButton={true}
              tooltipConfig={{
                tooltipEvent: 'mouseenter',
                tooltipPlace: 'left'
              }}
            >
              {pixel.enabled
                ? formatMessage({
                    defaultMessage: 'Click to hide element from the campaign',
                    id: 'conversionTracking.button.hide'
                  })
                : formatMessage({
                    defaultMessage: 'Click to show element in the campaign',
                    id: 'conversionTracking.button.show'
                  })}
            </TooltipButton>
          ),
          id: `custom-action-show-tooltip-${pixel.pixelId}`,
          isCustomAction: true
        }
      ],
      creationDate: formatDate(userPixel?.created ? userPixel.created : new Date(), 'dayOf'),
      id: pixel.pixelId,
      postClickWindow: String(pixel.postClickWindow),
      postViewWindow: String(pixel.postViewWindow),
      title: userPixel?.name || 'N/A',
      type: formatMessage({
        defaultMessage:
          String(userPixel?.type).toLowerCase() === 'stay'
            ? 'Permanence'
            : userPixel?.type && userPixel.type.charAt(0).toUpperCase() + String(userPixel.type).slice(1),
        id: `conversionTracking.filters.typeOptions.${userPixel?.type}`
      })
    };
  });

  const isSectionOpened = !!(pixels && pixels.length > 0);
  const conversionTrackingTags = getConversionTrackingTags(pixels);

  return (
    <div className="conversionTracking" id={id}>
      <FormExpandableSection
        defaultOpen={isSectionOpened}
        onReset={handleResetConversionTracking}
        preffixId={id}
        tags={conversionTrackingTags}
        title={formatMessage({
          defaultMessage: 'Conversion Tracking',
          id: 'conversionTracking.title'
        })}
      >
        <div className="conversionTracking__boxBody">
          <div className="conversionTracking__body">
            <div className="conversionTracking__contentOptions">
              <div className="conversionTracking__inputSearchContainer">
                <InputSearch
                  className="conversionTracking__inputSearch"
                  id={`conversionTracking-searchByName-$id`}
                  lens
                  name="conversionTracking"
                  onChange={handleSearchChange}
                  placeholder={formatMessage({
                    defaultMessage: 'Search by name, pixel type...',
                    id: 'conversionTracking.table.filter.search'
                  })}
                />
              </div>
              <span className="conversionTracking__actionsContainer">
                <div className="conversionTracking__showItemsLabel">
                  {formatMessage({
                    defaultMessage: 'Show items:',
                    id: 'conversionTracking.table.filter.showItems'
                  })}
                </div>
                <MultipleTagSelector
                  bgColor="rgba(133, 170, 236, 0.05)"
                  borderColor="#7B9FE1"
                  className="conversionTracking__multipleTagSelector"
                  items={[
                    {
                      label: formatMessage({
                        defaultMessage: 'All',
                        id: 'conversionTracking.table.filter.all'
                      }),
                      value: 'all'
                    },
                    {
                      label: formatMessage({
                        defaultMessage: 'Only visible',
                        id: 'conversionTracking.table.filter.onlyVisible'
                      }),
                      value: 'enabled'
                    },
                    {
                      label: formatMessage({
                        defaultMessage: 'Only hidden',
                        id: 'conversionTracking.table.filter.onlyHidden'
                      }),
                      value: 'disabled'
                    }
                  ]}
                  onChange={handleStatusChange}
                  selectedItems={[statusFilter]}
                  selectionType="simple"
                  textColor="#7B9FE1"
                />
                <ButtonUI
                  className="conversionTracking__addPixelsButton"
                  icon="fa-solid fa-plus"
                  onClick={() =>
                    setState(prev => ({
                      ...prev,
                      showModal: true
                    }))
                  }
                  size="small"
                  text="Add Pixels"
                  type="primary"
                />
              </span>
            </div>
            <div className="conversionTracking__contentTable">
              <TaptapList
                className="conversionTracking__list"
                data={compositionData}
                id={`conversionTracking-table-${id}`}
              >
                <TaptapList.ColumnsBar>
                  <TaptapList.ColumnsBar.Column
                    className="pixelTitle"
                    description={formatMessage({
                      defaultMessage: 'Pixel',
                      id: 'conversionTracking.table.columns.pixel'
                    })}
                    id="pixelTitle"
                  />
                  <TaptapList.ColumnsBar.Column
                    className="sonataPid"
                    description={formatMessage({
                      defaultMessage: 'Sonata PID',
                      id: 'conversionTracking.table.columns.sonataPid'
                    })}
                    id="sonataPid"
                  />
                  <TaptapList.ColumnsBar.Column
                    className="type"
                    description={formatMessage({
                      defaultMessage: 'Pixel Type',
                      id: 'conversionTracking.table.columns.pixelType'
                    })}
                    id="pixelType"
                  />
                  <TaptapList.ColumnsBar.Column
                    className="creationData"
                    description={formatMessage({
                      defaultMessage: 'Creation Data',
                      id: 'conversionTracking.table.columns.creationData'
                    })}
                    id="creationData"
                  />
                  <TaptapList.ColumnsBar.Column
                    className="postViewWindow"
                    description={formatMessage({
                      defaultMessage: 'Post-View Window',
                      id: 'conversionTracking.table.columns.postViewWindow'
                    })}
                    id="postViewWindow"
                  />
                  <TaptapList.ColumnsBar.Column
                    className="postClickWindow"
                    description={formatMessage({
                      defaultMessage: 'Post-Click Window',
                      id: 'conversionTracking.table.columns.postClickWindow'
                    })}
                    id="postClickWindow"
                  />
                </TaptapList.ColumnsBar>
                <TaptapList.RowsList<TaptapListItem>
                  emptyStateDescription={formatMessage({
                    defaultMessage: 'Try adjusting your search or filters to find pixels.',
                    id: 'conversionTracking.emptyState.description'
                  })}
                  emptyStateIcon="fal fa-empty-set"
                  emptyStateTitle={formatMessage({
                    defaultMessage: 'No results match your filters',
                    id: 'conversionTracking.emptyState.title'
                  })}
                >
                  {itemsList =>
                    itemsList.map(item => (
                      <TaptapList.RowsList.Row id={item.id} key={item.id}>
                        <span className="pixelTitle">{item.title}</span>
                        <span className="sonataPid">{item.id}</span>
                        <span className="type">{item.type}</span>
                        <span className="creationDate">{item.creationDate}</span>
                        <span className="postViewWindow">
                          <Input
                            className="postViewWindowInput"
                            id={`postViewInput-${id}`}
                            name="postView"
                            notFloatingPlaceholder
                            onChange={value => {
                              handleUpdatePixels(item.id, { postViewWindow: Number(value) });
                            }}
                            shape="squareUI"
                            tiny
                            type="number"
                            value={item.postViewWindow}
                          />
                        </span>
                        <span className="postClickWindow">
                          <Input
                            className="postClickWindowInput"
                            id={`postClickInput-${id}`}
                            name="postClick"
                            notFloatingPlaceholder
                            onChange={value => {
                              handleUpdatePixels(item.id, { postClickWindow: Number(value) });
                            }}
                            shape="squareUI"
                            tiny
                            type="number"
                            value={item.postClickWindow}
                          />
                        </span>
                        <TaptapList.RowsList.Row.Actions actions={item.actions} />
                      </TaptapList.RowsList.Row>
                    ))
                  }
                </TaptapList.RowsList>
              </TaptapList>
            </div>
          </div>
        </div>
      </FormExpandableSection>
      {showModal && (
        <ConversionTrackingModal
          isModalOpened={showModal}
          onCloseModal={() =>
            setState(prev => ({
              ...prev,
              showModal: false
            }))
          }
          onSavePixels={handlePixelsSave}
          selectedPixels={selectedPixelsRows.map(p => p.pixelId)}
        />
      )}
    </div>
  );
}

export default ConversionTracking;
